import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wrench, Plus, Edit, Trash2, Search, ChevronDown, ChevronRight, 
  Loader2, AlertTriangle, Save, Scale, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ToolAndDie = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Data
  const [finishedParts, setFinishedParts] = useState([]);
  const [tools, setTools] = useState([]);
  const [expandedPart, setExpandedPart] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOrientation, setFilterOrientation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialogs
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [editingTool, setEditingTool] = useState(null);
  const [selectedPartForTool, setSelectedPartForTool] = useState(null);
  
  // Form data
  const [partFormData, setPartFormData] = useState({
    finished_part_number: '',
    finished_part_description: '',
    customer: '',
    process_type: '',
    notes: ''
  });
  
  const [toolFormData, setToolFormData] = useState({
    tool_part_number: '',
    tool_description: '',
    finished_part_number: '',
    orientation: 'Vertical',
    tool_type: 'Top Die',
    diameter: '',
    height: '',
    length: '',
    width: '',
    material: 'Tool Steel',
    status: 'Active',
    notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, partsRes, toolsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tool-die/config`),
        axios.get(`${BACKEND_URL}/api/tool-die/stats`),
        axios.get(`${BACKEND_URL}/api/tool-die/finished-parts`),
        axios.get(`${BACKEND_URL}/api/tool-die/tools`)
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
      setFinishedParts(partsRes.data.sort((a, b) => a.finished_part_number.localeCompare(b.finished_part_number)));
      setTools(toolsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load Tool and Die data');
    } finally {
      setLoading(false);
    }
  };

  const getToolsForPart = (partNumber) => {
    return tools.filter(t => t.finished_part_number === partNumber);
  };

  const getPartToolingWeight = (partNumber) => {
    const partTools = getToolsForPart(partNumber);
    return partTools.reduce((sum, t) => sum + (t.calculated_weight || 0), 0);
  };

  // Filter parts
  const filteredParts = finishedParts.filter(part => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const partTools = getToolsForPart(part.finished_part_number);
      const matchesPart = part.finished_part_number.toLowerCase().includes(query) ||
                         part.finished_part_description?.toLowerCase().includes(query) ||
                         part.customer?.toLowerCase().includes(query);
      const matchesTool = partTools.some(t => 
        t.tool_part_number.toLowerCase().includes(query) ||
        t.tool_description?.toLowerCase().includes(query)
      );
      if (!matchesPart && !matchesTool) return false;
    }
    return true;
  });

  // CRUD Operations
  const handleSavePart = async () => {
    if (!partFormData.finished_part_number || !partFormData.finished_part_description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingPart) {
        await axios.put(`${BACKEND_URL}/api/tool-die/finished-parts/${editingPart.finished_part_number}`, partFormData);
        toast.success('Finished part updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/tool-die/finished-parts`, partFormData);
        toast.success('Finished part created');
      }
      setPartDialogOpen(false);
      setEditingPart(null);
      resetPartForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save finished part');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePart = async (partNumber) => {
    if (!confirm(`Delete ${partNumber} and all associated tools?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/tool-die/finished-parts/${partNumber}`);
      toast.success('Finished part deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete finished part');
    }
  };

  const handleSaveTool = async () => {
    if (!toolFormData.tool_part_number || !toolFormData.tool_description || !toolFormData.height) {
      toast.error('Please fill in required fields');
      return;
    }
    
    // Validate dimensions based on orientation
    if (toolFormData.orientation === 'Vertical' && !toolFormData.diameter) {
      toast.error('Diameter is required for vertical tooling');
      return;
    }
    if (toolFormData.orientation === 'Horizontal' && (!toolFormData.length || !toolFormData.width)) {
      toast.error('Length and Width are required for horizontal tooling');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...toolFormData,
        diameter: toolFormData.diameter ? parseFloat(toolFormData.diameter) : null,
        height: parseFloat(toolFormData.height),
        length: toolFormData.length ? parseFloat(toolFormData.length) : null,
        width: toolFormData.width ? parseFloat(toolFormData.width) : null
      };
      
      if (editingTool) {
        await axios.put(`${BACKEND_URL}/api/tool-die/tools/${editingTool.tool_id}`, payload);
        toast.success('Tool updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/tool-die/tools`, payload);
        toast.success('Tool created');
      }
      setToolDialogOpen(false);
      setEditingTool(null);
      resetToolForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tool');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!confirm('Delete this tool?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/tool-die/tools/${toolId}`);
      toast.success('Tool deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete tool');
    }
  };

  const resetPartForm = () => {
    setPartFormData({
      finished_part_number: '',
      finished_part_description: '',
      customer: '',
      process_type: '',
      notes: ''
    });
  };

  const resetToolForm = () => {
    setToolFormData({
      tool_part_number: '',
      tool_description: '',
      finished_part_number: selectedPartForTool || '',
      orientation: 'Vertical',
      tool_type: 'Top Die',
      diameter: '',
      height: '',
      length: '',
      width: '',
      material: 'Tool Steel',
      status: 'Active',
      notes: ''
    });
  };

  const openEditPart = (part) => {
    setEditingPart(part);
    setPartFormData({
      finished_part_number: part.finished_part_number,
      finished_part_description: part.finished_part_description,
      customer: part.customer || '',
      process_type: part.process_type || '',
      notes: part.notes || ''
    });
    setPartDialogOpen(true);
  };

  const openEditTool = (tool) => {
    setEditingTool(tool);
    setToolFormData({
      tool_part_number: tool.tool_part_number,
      tool_description: tool.tool_description,
      finished_part_number: tool.finished_part_number,
      orientation: tool.orientation,
      tool_type: tool.tool_type,
      diameter: tool.diameter?.toString() || '',
      height: tool.height?.toString() || '',
      length: tool.length?.toString() || '',
      width: tool.width?.toString() || '',
      material: tool.material || 'Tool Steel',
      status: tool.status,
      notes: tool.notes || ''
    });
    setToolDialogOpen(true);
  };

  const openAddTool = (partNumber) => {
    setSelectedPartForTool(partNumber);
    setEditingTool(null);
    setToolFormData({
      ...toolFormData,
      finished_part_number: partNumber,
      tool_part_number: '',
      tool_description: ''
    });
    setToolDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'Spare': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'Under Repair': return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      case 'Scrapped': return 'bg-red-500/20 text-red-400 border-red-500/50';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
    }
  };

  const getToolTypes = () => {
    if (!config) return [];
    return toolFormData.orientation === 'Vertical' 
      ? config.vertical_tool_types 
      : config.horizontal_tool_types;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-orange-500/20 rounded-lg p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Finished Parts</p>
          <p className="text-2xl font-bold text-white">{stats?.total_finished_parts || 0}</p>
        </div>
        <div className="bg-slate-800/50 border border-orange-500/20 rounded-lg p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Tools</p>
          <p className="text-2xl font-bold text-white">{stats?.total_tools || 0}</p>
        </div>
        <div className="bg-slate-800/50 border border-orange-500/20 rounded-lg p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Weight</p>
          <p className="text-2xl font-bold text-orange-400">{stats?.total_weight_kg?.toLocaleString() || 0} kg</p>
        </div>
        <div className="bg-slate-800/50 border border-orange-500/20 rounded-lg p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Active Tools</p>
          <p className="text-2xl font-bold text-green-400">{stats?.status_breakdown?.Active || 0}</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search parts or tools..."
              className="pl-10 bg-slate-800/50 border-slate-700"
              data-testid="tool-die-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {config?.statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAllData} className="border-slate-700">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <Button
          onClick={() => { setEditingPart(null); resetPartForm(); setPartDialogOpen(true); }}
          className="bg-orange-600 hover:bg-orange-500"
          data-testid="add-finished-part-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Finished Part
        </Button>
      </div>

      {/* Parts List */}
      <div className="space-y-3">
        {filteredParts.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No finished parts found</p>
            <p className="text-sm">Add a finished part to start managing tooling</p>
          </div>
        ) : (
          filteredParts.map((part) => {
            const partTools = getToolsForPart(part.finished_part_number);
            const partWeight = getPartToolingWeight(part.finished_part_number);
            const isExpanded = expandedPart === part.finished_part_number;
            
            // Apply filters to tools
            const filteredTools = partTools.filter(t => {
              if (filterOrientation !== 'all' && t.orientation !== filterOrientation) return false;
              if (filterStatus !== 'all' && t.status !== filterStatus) return false;
              return true;
            });

            return (
              <div key={part.finished_part_number} className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
                {/* Part Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-slate-800/30 transition-colors flex items-center justify-between"
                  onClick={() => setExpandedPart(isExpanded ? null : part.finished_part_number)}
                  data-testid={`part-row-${part.finished_part_number}`}
                >
                  <div className="flex items-center gap-4">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-orange-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                    <div>
                      <h3 className="font-mono font-bold text-white">{part.finished_part_number}</h3>
                      <p className="text-sm text-slate-400">{part.finished_part_description}</p>
                      {part.customer && <p className="text-xs text-slate-500">Customer: {part.customer}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">{partTools.length} tools</p>
                      <p className="text-sm font-medium text-orange-400">{partWeight.toFixed(2)} kg</p>
                    </div>
                    <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => openAddTool(part.finished_part_number)} className="text-green-400 hover:bg-green-500/10">
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditPart(part)} className="text-blue-400 hover:bg-blue-500/10">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeletePart(part.finished_part_number)} className="text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Tools List */}
                {isExpanded && (
                  <div className="border-t border-slate-800 bg-slate-950/50">
                    {filteredTools.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <p>No tools found for this part</p>
                        <Button size="sm" variant="outline" onClick={() => openAddTool(part.finished_part_number)} className="mt-2 border-orange-500/50 text-orange-400">
                          <Plus className="w-4 h-4 mr-2" /> Add Tool
                        </Button>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-800/50">
                        {/* Tools Header */}
                        <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs text-slate-500 uppercase tracking-wider bg-slate-900/30">
                          <div className="col-span-2">Tool Part #</div>
                          <div className="col-span-2">Type</div>
                          <div className="col-span-1">Orient.</div>
                          <div className="col-span-2">Dimensions</div>
                          <div className="col-span-1">Material</div>
                          <div className="col-span-1 text-right">Weight</div>
                          <div className="col-span-1">Status</div>
                          <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {filteredTools.map((tool) => (
                          <div key={tool.tool_id} className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-800/20" data-testid={`tool-row-${tool.tool_id}`}>
                            <div className="col-span-2">
                              <p className="font-mono text-sm text-white">{tool.tool_part_number}</p>
                              <p className="text-xs text-slate-500 truncate">{tool.tool_description}</p>
                            </div>
                            <div className="col-span-2 text-sm text-slate-300">{tool.tool_type}</div>
                            <div className="col-span-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${tool.orientation === 'Vertical' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                {tool.orientation}
                              </span>
                            </div>
                            <div className="col-span-2 text-sm text-slate-400 font-mono">
                              {tool.orientation === 'Vertical' ? (
                                <span>Ø{tool.diameter} × {tool.height}mm</span>
                              ) : (
                                <span>{tool.length}×{tool.width}×{tool.height}mm</span>
                              )}
                            </div>
                            <div className="col-span-1 text-sm text-slate-400">{tool.material}</div>
                            <div className="col-span-1 text-right">
                              <span className="font-medium text-orange-400">{tool.calculated_weight?.toFixed(2)} kg</span>
                            </div>
                            <div className="col-span-1">
                              <span className={`text-xs px-2 py-0.5 rounded border ${getStatusColor(tool.status)}`}>
                                {tool.status}
                              </span>
                            </div>
                            <div className="col-span-2 flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEditTool(tool)} className="text-blue-400 hover:bg-blue-500/10 h-7 w-7 p-0">
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteTool(tool.tool_id)} className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Finished Part Dialog */}
      <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
        <DialogContent className="bg-slate-900 border-orange-500/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-400" />
              {editingPart ? 'Edit Finished Part' : 'Add Finished Part'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingPart ? 'Update finished part details' : 'Add a new finished part to manage its tooling'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Part Number *</Label>
                <Input
                  value={partFormData.finished_part_number}
                  onChange={(e) => setPartFormData({ ...partFormData, finished_part_number: e.target.value.toUpperCase() })}
                  className="bg-slate-800 border-slate-700 mt-1 font-mono"
                  placeholder="e.g., FP-001"
                  disabled={!!editingPart}
                  data-testid="part-number-input"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Customer</Label>
                <Input
                  value={partFormData.customer}
                  onChange={(e) => setPartFormData({ ...partFormData, customer: e.target.value })}
                  className="bg-slate-800 border-slate-700 mt-1"
                  placeholder="Customer name"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Description *</Label>
              <Input
                value={partFormData.finished_part_description}
                onChange={(e) => setPartFormData({ ...partFormData, finished_part_description: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1"
                placeholder="Part description"
                data-testid="part-description-input"
              />
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Process Type</Label>
              <Input
                value={partFormData.process_type}
                onChange={(e) => setPartFormData({ ...partFormData, process_type: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1"
                placeholder="e.g., Forging, Machining"
              />
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Notes</Label>
              <Textarea
                value={partFormData.notes}
                onChange={(e) => setPartFormData({ ...partFormData, notes: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1"
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setPartDialogOpen(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button onClick={handleSavePart} disabled={submitting} className="bg-orange-600 hover:bg-orange-500">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingPart ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Tool Dialog */}
      <Dialog open={toolDialogOpen} onOpenChange={setToolDialogOpen}>
        <DialogContent className="bg-slate-900 border-orange-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Scale className="w-5 h-5 text-orange-400" />
              {editingTool ? 'Edit Tool' : 'Add Tool'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingTool ? 'Update tool details and dimensions' : 'Add a new tool or die to this finished part'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Tool Part Number *</Label>
                <Input
                  value={toolFormData.tool_part_number}
                  onChange={(e) => setToolFormData({ ...toolFormData, tool_part_number: e.target.value.toUpperCase() })}
                  className="bg-slate-800 border-slate-700 mt-1 font-mono"
                  placeholder="e.g., TOOL-001"
                  data-testid="tool-part-number-input"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Finished Part</Label>
                <Input
                  value={toolFormData.finished_part_number}
                  className="bg-slate-800 border-slate-700 mt-1 font-mono"
                  disabled
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Description *</Label>
              <Input
                value={toolFormData.tool_description}
                onChange={(e) => setToolFormData({ ...toolFormData, tool_description: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1"
                placeholder="Tool description"
                data-testid="tool-description-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Orientation *</Label>
                <Select 
                  value={toolFormData.orientation} 
                  onValueChange={(v) => setToolFormData({ 
                    ...toolFormData, 
                    orientation: v,
                    tool_type: v === 'Vertical' ? 'Top Die' : 'Top Die'
                  })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.orientations?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Tool Type *</Label>
                <Select value={toolFormData.tool_type} onValueChange={(v) => setToolFormData({ ...toolFormData, tool_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getToolTypes().map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Status</Label>
                <Select value={toolFormData.status} onValueChange={(v) => setToolFormData({ ...toolFormData, status: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Dimensions - conditional based on orientation */}
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">
                Dimensions ({toolFormData.orientation === 'Vertical' ? 'Cylindrical' : 'Block'}) - All in mm
              </p>
              {toolFormData.orientation === 'Vertical' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 uppercase text-xs">Diameter (mm) *</Label>
                    <Input
                      type="number"
                      value={toolFormData.diameter}
                      onChange={(e) => setToolFormData({ ...toolFormData, diameter: e.target.value })}
                      className="bg-slate-900 border-slate-600 mt-1"
                      placeholder="e.g., 150"
                      data-testid="diameter-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 uppercase text-xs">Height (mm) *</Label>
                    <Input
                      type="number"
                      value={toolFormData.height}
                      onChange={(e) => setToolFormData({ ...toolFormData, height: e.target.value })}
                      className="bg-slate-900 border-slate-600 mt-1"
                      placeholder="e.g., 200"
                      data-testid="height-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-400 uppercase text-xs">Length (mm) *</Label>
                    <Input
                      type="number"
                      value={toolFormData.length}
                      onChange={(e) => setToolFormData({ ...toolFormData, length: e.target.value })}
                      className="bg-slate-900 border-slate-600 mt-1"
                      placeholder="e.g., 300"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 uppercase text-xs">Width (mm) *</Label>
                    <Input
                      type="number"
                      value={toolFormData.width}
                      onChange={(e) => setToolFormData({ ...toolFormData, width: e.target.value })}
                      className="bg-slate-900 border-slate-600 mt-1"
                      placeholder="e.g., 200"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 uppercase text-xs">Height (mm) *</Label>
                    <Input
                      type="number"
                      value={toolFormData.height}
                      onChange={(e) => setToolFormData({ ...toolFormData, height: e.target.value })}
                      className="bg-slate-900 border-slate-600 mt-1"
                      placeholder="e.g., 150"
                      data-testid="height-input"
                    />
                  </div>
                </div>
              )}
              
              {/* Weight Preview */}
              {((toolFormData.orientation === 'Vertical' && toolFormData.diameter && toolFormData.height) ||
                (toolFormData.orientation === 'Horizontal' && toolFormData.length && toolFormData.width && toolFormData.height)) && (
                <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase mb-1">Calculated Weight</p>
                  <p className="text-xl font-bold text-orange-400">
                    {toolFormData.orientation === 'Vertical'
                      ? (Math.PI * Math.pow(parseFloat(toolFormData.diameter) / 2000, 2) * (parseFloat(toolFormData.height) / 1000) * 7850).toFixed(2)
                      : ((parseFloat(toolFormData.length) / 1000) * (parseFloat(toolFormData.width) / 1000) * (parseFloat(toolFormData.height) / 1000) * 7850).toFixed(2)
                    } kg
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Material</Label>
                <Input
                  value={toolFormData.material}
                  onChange={(e) => setToolFormData({ ...toolFormData, material: e.target.value })}
                  className="bg-slate-800 border-slate-700 mt-1"
                  placeholder="Tool Steel"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Notes</Label>
                <Input
                  value={toolFormData.notes}
                  onChange={(e) => setToolFormData({ ...toolFormData, notes: e.target.value })}
                  className="bg-slate-800 border-slate-700 mt-1"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setToolDialogOpen(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button onClick={handleSaveTool} disabled={submitting} className="bg-orange-600 hover:bg-orange-500">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingTool ? 'Update Tool' : 'Create Tool'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ToolAndDie;
